import { useEffect, useState } from "react";
import {
  addDoc,
  collection,
  getDocs,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Collapse,
} from "react-bootstrap";
import {
  FaPlusCircle,
  FaArrowLeft,
  FaBullseye,
  FaShieldAlt,
  FaUsers,
  FaSearch,
} from "react-icons/fa";

import { db } from "../firebase";
import Header from "../components/Header";

function AddCall() {
  const navigate = useNavigate();

  const [stockName, setStockName] = useState("");
  const [callType, setCallType] = useState("Long Term");

  // These fields can contain words, numbers, ranges or any format.
  const [buyPrice, setBuyPrice] = useState("");
  const [targetPrice, setTargetPrice] = useState("");
  const [stopLoss, setStopLoss] = useState("");

  // These fields are used for P/L calculation.
  const [entryPrice, setEntryPrice] = useState("");
  const [currentPrice, setCurrentPrice] = useState("");

  // Customer selection.
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(true);

  // Compact customer dropdown and search.
  const [showCustomerList, setShowCustomerList] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadCustomers = async () => {
      try {
        setCustomersLoading(true);

        const customerSnapshot = await getDocs(
          collection(db, "customers")
        );

        const customerList = customerSnapshot.docs.map(
          (customerDocument) => ({
            id: customerDocument.id,
            ...customerDocument.data(),
          })
        );

        customerList.sort((firstCustomer, secondCustomer) =>
          String(firstCustomer.name || "").localeCompare(
            String(secondCustomer.name || "")
          )
        );

        setCustomers(customerList);

        // All customers are selected automatically.
        setSelectedCustomerIds(
          customerList.map((customer) => customer.id)
        );
      } catch (error) {
        console.error("Unable to load customers:", error);
        alert("Unable to load customer list.");
      } finally {
        setCustomersLoading(false);
      }
    };

    loadCustomers();
  }, []);

  const getTodayDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  const getCurrentTime = () => {
    return new Date().toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleCustomerSelection = (customerId) => {
    setSelectedCustomerIds((previousSelectedIds) => {
      if (previousSelectedIds.includes(customerId)) {
        return previousSelectedIds.filter(
          (selectedId) => selectedId !== customerId
        );
      }

      return [...previousSelectedIds, customerId];
    });
  };

  const selectAllCustomers = () => {
    setSelectedCustomerIds(
      customers.map((customer) => customer.id)
    );
  };

  const clearAllCustomers = () => {
    setSelectedCustomerIds([]);
  };

  const filteredCustomers = customers.filter((customer) => {
    const searchText = customerSearch.trim().toLowerCase();

    if (!searchText) {
      return true;
    }

    const customerName = String(
      customer.name || ""
    ).toLowerCase();

    const customerEmail = String(
      customer.email || ""
    ).toLowerCase();

    return (
      customerName.includes(searchText) ||
      customerEmail.includes(searchText)
    );
  });

  const addStockCall = async () => {
    try {
      if (
        !stockName ||
        !callType ||
        !buyPrice ||
        !entryPrice ||
        !currentPrice
      ) {
        alert(
          "Please fill Stock Name, Call Type, Buy Price, Entry Price and Current Price."
        );
        return;
      }

      setLoading(true);

      // Customers who were unchecked will be excluded from this call.
      const excludedCustomerIds = customers
        .filter(
          (customer) =>
            !selectedCustomerIds.includes(customer.id)
        )
        .map((customer) => customer.id);

      const currentTimestamp = Date.now();

      await addDoc(collection(db, "stockCalls"), {
        stockName: stockName.trim().toUpperCase(),
        callType,

        buyPrice,
        targetPrice,
        stopLoss,

        entryPrice: Number(entryPrice),
        currentPrice: Number(currentPrice),

        // Every newly uploaded call is active automatically.
        status: "Active",

        // Customers in this list should not receive this call.
        excludedCustomerIds,

        recommendationDate: getTodayDate(),
        recommendationTime: getCurrentTime(),
        createdAt: currentTimestamp,
        updatedAt: currentTimestamp,
      });

      alert("✅ Stock call added successfully.");

      navigate("/admin-dashboard");
    } catch (error) {
      console.error("Unable to add stock call:", error);
      alert("Unable to add stock call.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />

      <Container className="mt-4">
        <Row className="justify-content-center">
          <Col lg={8}>
            <Card
              className="shadow border-0"
              style={{ borderRadius: "18px" }}
            >
              <Card.Body className="p-4">
                <h3 className="fw-bold mb-2">
                  <FaPlusCircle className="me-2 text-success" />
                  Add New Stock Call
                </h3>

                <p className="text-muted mb-4">
                  Add a new stock recommendation. Target and Stop Loss
                  will stay hidden from customers until admin confirms
                  customer investment from View Trades.
                </p>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Stock Name</Form.Label>

                      <Form.Control
                        type="text"
                        placeholder="Example: RELIANCE"
                        value={stockName}
                        onChange={(event) =>
                          setStockName(event.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Call Type</Form.Label>

                      <Form.Select
                        value={callType}
                        onChange={(event) =>
                          setCallType(event.target.value)
                        }
                      >
                        <option>Long Term</option>
                        <option>Mid Term</option>
                        <option>Short Term</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Buy Price</Form.Label>

                      <Form.Control
                        type="text"
                        placeholder="Example: Buy around 100 to 105"
                        value={buyPrice}
                        onChange={(event) =>
                          setBuyPrice(event.target.value)
                        }
                      />

                      <small className="text-muted">
                        Customer will see this as your call price text.
                      </small>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Entry Price</Form.Label>

                      <Form.Control
                        type="number"
                        placeholder="Example: 100"
                        value={entryPrice}
                        onChange={(event) =>
                          setEntryPrice(event.target.value)
                        }
                      />

                      <small className="text-muted">
                        Used for P/L calculation.
                      </small>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Current Price</Form.Label>

                      <Form.Control
                        type="number"
                        placeholder="Example: 120"
                        value={currentPrice}
                        onChange={(event) =>
                          setCurrentPrice(event.target.value)
                        }
                      />

                      <small className="text-muted">
                        Used for P/L calculation.
                      </small>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FaBullseye className="me-2 text-primary" />
                        Target Price
                      </Form.Label>

                      <Form.Control
                        type="text"
                        placeholder="Example: Target 1: 120, Target 2: 140"
                        value={targetPrice}
                        onChange={(event) =>
                          setTargetPrice(event.target.value)
                        }
                      />

                      <small className="text-muted">
                        Can be words, numbers or multiple targets.
                      </small>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FaShieldAlt className="me-2 text-danger" />
                        Stop Loss
                      </Form.Label>

                      <Form.Control
                        type="text"
                        placeholder="Example: SL below 95"
                        value={stopLoss}
                        onChange={(event) =>
                          setStopLoss(event.target.value)
                        }
                      />

                      <small className="text-muted">
                        Can be words, numbers or any format.
                      </small>
                    </Form.Group>
                  </Col>
                </Row>

                <Card
                  className="border mb-4"
                  style={{
                    borderRadius: "14px",
                    backgroundColor: "#f8f9fa",
                  }}
                >
                  <Card.Body>
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                      <div>
                        <h5 className="fw-bold mb-1">
                          <FaUsers className="me-2 text-primary" />
                          Select Customers
                        </h5>

                        <small className="text-muted">
                          All customers are selected automatically.
                          Uncheck only those who should not receive this
                          call.
                        </small>
                      </div>

                      <Button
                        type="button"
                        variant="outline-primary"
                        size="sm"
                        onClick={() =>
                          setShowCustomerList(
                            (previousValue) => !previousValue
                          )
                        }
                        aria-controls="customer-selection-list"
                        aria-expanded={showCustomerList}
                        disabled={customersLoading}
                      >
                        {customersLoading
                          ? "Loading..."
                          : showCustomerList
                          ? "Hide Customers"
                          : `Choose Customers (${selectedCustomerIds.length}/${customers.length})`}
                      </Button>
                    </div>

                    <Collapse in={showCustomerList}>
                      <div id="customer-selection-list">
                        <hr />

                        {customersLoading ? (
                          <div className="text-center py-3">
                            <Spinner
                              animation="border"
                              size="sm"
                              className="me-2"
                            />
                            Loading customers...
                          </div>
                        ) : customers.length === 0 ? (
                          <p className="text-muted mb-0">
                            No customers are available.
                          </p>
                        ) : (
                          <>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                <FaSearch className="me-2" />
                                Search Customer
                              </Form.Label>

                              <Form.Control
                                type="search"
                                placeholder="Search by customer name or email"
                                value={customerSearch}
                                onChange={(event) =>
                                  setCustomerSearch(
                                    event.target.value
                                  )
                                }
                              />
                            </Form.Group>

                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                              <strong>
                                Selected:{" "}
                                {selectedCustomerIds.length} of{" "}
                                {customers.length}
                              </strong>

                              <div className="d-flex gap-2">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline-success"
                                  onClick={selectAllCustomers}
                                >
                                  Select All
                                </Button>

                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline-secondary"
                                  onClick={clearAllCustomers}
                                >
                                  Clear All
                                </Button>
                              </div>
                            </div>

                            <div
                              style={{
                                maxHeight: "240px",
                                overflowY: "auto",
                              }}
                            >
                              {filteredCustomers.length === 0 ? (
                                <div className="text-center text-muted py-3">
                                  No customer found.
                                </div>
                              ) : (
                                filteredCustomers.map((customer) => (
                                  <div
                                    key={customer.id}
                                    className="border rounded p-3 mb-2 bg-white"
                                  >
                                    <Form.Check
                                      type="checkbox"
                                      id={`customer-${customer.id}`}
                                      checked={selectedCustomerIds.includes(
                                        customer.id
                                      )}
                                      onChange={() =>
                                        handleCustomerSelection(
                                          customer.id
                                        )
                                      }
                                      label={
                                        <div>
                                          <div className="fw-semibold">
                                            {customer.name ||
                                              "Unnamed Customer"}
                                          </div>

                                          <small className="text-muted">
                                            {customer.email ||
                                              "No email"}

                                            {customer.plan
                                              ? ` • ${customer.plan}`
                                              : ""}

                                            {customer.status
                                              ? ` • ${customer.status}`
                                              : ""}
                                          </small>
                                        </div>
                                      }
                                    />
                                  </div>
                                ))
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </Collapse>
                  </Card.Body>
                </Card>

                <Row>
                  <Col md={6} className="mb-2">
                    <Button
                      variant="outline-dark"
                      className="w-100"
                      onClick={() =>
                        navigate("/admin-dashboard")
                      }
                    >
                      <FaArrowLeft className="me-2" />
                      Back
                    </Button>
                  </Col>

                  <Col md={6} className="mb-2">
                    <Button
                      variant="success"
                      className="w-100"
                      onClick={addStockCall}
                      disabled={loading || customersLoading}
                    >
                      <FaPlusCircle className="me-2" />

                      {loading
                        ? "Adding..."
                        : "Add Stock Call"}
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default AddCall;